import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { supabaseAdmin } from '@/lib/supabaseAdminClient';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useProject } from '@/contexts/ProjectContext';

type AuthUser = { email: string; last_sign_in_at: string | null };

interface Club {
  id: string;
  name: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  club_id: string;
  club_name?: string;
  last_active_at?: string;
  status: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [form, setForm] = useState({ name: '', email: '', club_id: '', role: 'captain' });
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { profile: userProfile } = useAuth() as any;
  const { id: projectId, slug: projectSlug } = useProject();

  const [sortField, setSortField] = useState<keyof User | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchClubs();
  }, []);

  const fetchUsers = async () => {
    const { data: userData, error } = await supabase
      .from('users')
      .select('id, name, email, role, club_id, status, last_active_at, clubs(name)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (!error && userData) {
      type AuthListUsersResponse = { users: AuthUser[] };
      const { data: authData } = await supabaseAdmin.auth.admin.listUsers() as { data: AuthListUsersResponse };
      const mapped = userData.map((u: any) => ({
        ...u,
        club_name: u.clubs?.name || '—',
      }));
      setUsers(mapped.filter((u: any) => u.email));
    }
  };

  const fetchClubs = async () => {
    const { data } = await supabase
      .from('clubs')
      .select('id, name')
      .eq('project_id', projectId)
      .order('name');
    if (data) setClubs(data);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (value: string) => {
    setForm({ ...form, role: value });
  };

  const handleClubChange = (value: string) => {
    setForm({ ...form, club_id: value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseAdmin.auth.admin.inviteUserByEmail(form.email);

      if (error || !data?.user) {
        console.error('Failed to invite user:', error?.message || 'No user returned');
        setLoading(false);
        return;
      }

      const { error: insertError } = await supabase
        .from('users')
        .insert({
          id: data.user.id, // Ensure this matches the auth.users ID
          email: form.email,
          club_id: form.club_id,
          role: form.role,
          name: form.name,
          project_id: projectId || null,
        });

      if (insertError) {
        console.error('Failed to insert into public.users:', insertError.message);
      } else {
        setForm({ name: '', email: '', club_id: '', role: 'captain' });
        fetchUsers();
        toast.success('User added and invite sent successfully.');
      }
    } catch (err) {
      console.error('Unexpected error creating user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId: string) => {
    const { error } = await supabase.from('users').delete().eq('id', userId);
    if (error) {
      toast.error('Failed to delete user.');
    } else {
      toast.success('User deleted successfully.');
      setUsers((prev) => prev.filter((user) => user.id !== userId));
    }
  };

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <main className="container mx-auto px-4 py-6 bg-[#FAFAFB] min-h-screen">
      <h1 className="text-3xl font-bold text-[#241F39] mb-6">User Management</h1>

      <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h2 className="text-xl font-semibold text-[#241F39] mb-4">User Sign Up Link</h2>
        <div className="flex items-center gap-2">
          <Input
            value="https://www.officialscoring.com/projects/login/signup"
            readOnly
            className="flex-1"
          />
          <button
            className="text-sm px-4 py-2 bg-[#30702F] text-white rounded hover:opacity-90 transition"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText('https://www.officialscoring.com/projects/login/signup');
                setCopied(true);
                toast.success('Copied');
                setTimeout(() => setCopied(false), 1500);
              } catch (err) {
                toast.error('Failed to copy');
              }
            }}
          >
            {copied ? '✓' : 'Copy'}
          </button>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-4">
        <h2 className="text-xl font-semibold text-[#241F39] mb-4">Existing Users</h2>
        <div className="mb-4">
          <Input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-sm"
          />
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm text-left border border-gray-200 bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-[#F5F5F5] text-[#241F39]">
                {['name', 'email', 'role', 'club'].map((field) => (
                  <th key={field} className="px-4 py-3 cursor-pointer" onClick={() => {
                    setSortDirection(prev =>
                      sortField === field && prev === 'asc' ? 'desc' : 'asc'
                    );
                    setSortField(field as keyof User);
                  }}>
                    {field.charAt(0).toUpperCase() + field.slice(1)} {sortField === field ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                  </th>
                ))}
                <th className="px-4 py-3 cursor-pointer" onClick={() => {
                  setSortDirection(prev => sortField === 'last_active_at' && prev === 'asc' ? 'desc' : 'asc');
                  setSortField('last_active_at');
                }}>Last Active {sortField === 'last_active_at' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</th>
                <th className="px-4 py-3 cursor-pointer" onClick={() => {
                  setSortDirection(prev => sortField === 'status' && prev === 'asc' ? 'desc' : 'asc');
                  setSortField('status');
                }}>
                  Status {sortField === 'status' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.sort((a, b) => {
                if (!sortField) return 0;
                const aValue = a[sortField] || '';
                const bValue = b[sortField] || '';
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
              }).map((user) => (
                <tr key={user.id} className="border-t">
                  <td className="px-4 py-3">{user.name}</td>
                  <td className="px-4 py-3">{user.email}</td>
                  <td className="px-4 py-3">
                    <Select
                      value={user.role}
                      onValueChange={async (newRole) => {
                        const updateData: any = { role: newRole };
                        if (newRole === 'admin') updateData.club_id = null;
                        if (newRole === 'captain' && !user.club_id) {
                          toast.error('Captains must be associated with a club.');
                          return;
                        }
                        const { error } = await supabase
                          .from('users')
                          .update(updateData)
                          .eq('id', user.id);

                        if (!error) {
                          setUsers(prev =>
                            prev.map(u =>
                              u.id === user.id
                                ? { ...u, role: newRole, club_name: newRole === 'admin' ? '—' : u.club_name }
                                : u
                            )
                          );
                          toast.success('Role updated');
                        } else {
                          toast.error('Failed to update role');
                        }
                      }}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="captain">Captain</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <Select
                      value={user.club_id || ''}
                      onValueChange={async (newClubId) => {
                        const { error } = await supabase
                          .from('users')
                          .update({ club_id: newClubId })
                          .eq('id', user.id);

                        if (!error) {
                          setUsers(prev =>
                            prev.map(u =>
                              u.id === user.id
                                ? { ...u, club_id: newClubId, club_name: clubs.find(c => c.id === newClubId)?.name || '—' }
                                : u
                            )
                          );
                          toast.success('Club updated');
                        } else {
                          toast.error('Failed to update club');
                        }
                      }}
                    >
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Select club" />
                      </SelectTrigger>
                      <SelectContent>
                        {clubs.map((club) => (
                          <SelectItem key={club.id} value={club.id}>
                            {club.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">{user.last_active_at ? new Date(user.last_active_at).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3">
                    <Select
                      value={user.status}
                      onValueChange={async (newStatus) => {
                        const { error } = await supabase
                          .from('users')
                          .update({ status: newStatus })
                          .eq('id', user.id);

                        if (!error) {
                          setUsers(prev =>
                            prev.map(u =>
                              u.id === user.id ? { ...u, status: newStatus } : u
                            )
                          );
                          toast.success('Status updated');
                        } else {
                          toast.error('Failed to update status');
                        }
                      }}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="deactivated">Deactivated</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
};

export default UserManagement;